/**
 * Online bridge for the heat model (pure, no engine deps). In online mode the SERVER owns heat
 * (server/police): the client keeps its police AI, witnesses and HUD for presentation, but
 *  - every `heat` message from the server overwrites the local HeatModel (`applyServerHeat`), so the
 *    HUD stars, the minimap search circle and the pursuit AI's last-known position show server state;
 *  - local crime scoring is not trusted: witnessed crimes are forwarded as `crime` reports
 *    (`crimeReport`) and the local model must not decay heat on its own (`localHeatAuthority`);
 *  - while the local police can see the player, the client sends `policeSeen` (only ever hurts it).
 *
 * client-integration wires this into src/net when the online session is up.
 */
import type { CrimeType } from '../core/events';
import type { HeatMsg } from '../../shared/messages/police';
import { SEARCH_DURATION, type HeatModel } from './heatModel';

/** Online: the server decides; offline: the local model does (single-player unchanged). */
export const localHeatAuthority = (online: boolean): boolean => !online;

/** Mirror a server `heat` message into the local model (HUD + police AI read the model). */
export function applyServerHeat(model: HeatModel, msg: HeatMsg, now: number): void {
  if (msg.level <= 0) {
    model.clear();
    return;
  }
  model.points = msg.points;
  model.level = msg.level;
  model.phase = msg.phase === 'none' ? 'search' : msg.phase;
  if (msg.lkp) model.suspect = { pos: { x: msg.lkp.x, z: msg.lkp.z }, time: now, vehicleId: model.suspect?.vehicleId ?? null };
  if (msg.phase === 'pursuit') {
    model.search = null;
    model.unseenFor = 0;
  } else if (msg.search) {
    const duration = SEARCH_DURATION[Math.max(1, Math.min(4, msg.level)) as 1 | 2 | 3 | 4];
    model.search = { center: { x: msg.search.x, z: msg.search.z }, radius: msg.search.r, elapsed: msg.search.p * duration, duration };
  }
}

/** A crime the local witness system scored, as the report the server accepts (it re-scores it). */
export function crimeReport<T extends CrimeType>(type: T, severity: number, police: number, civilians: number): { type: T; severity: number; police: number; civilians: number } {
  return {
    type,
    severity: Math.max(1, Math.min(5, Math.round(severity))),
    police: Math.max(0, Math.min(20, Math.round(police))),
    civilians: Math.max(0, Math.min(50, Math.round(civilians))),
  };
}
